import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types';

export type PlaceSummary = components['schemas']['PlaceSummaryReportResponse'];
export type InheritedRole = components['schemas']['InheritedRoleResponse'];

/**
 * How soon a pending invitation lapses to need attention. It is the contract's default, sent
 * anyway, so the page and the server can never disagree on what «in scadenza» means.
 */
export const EXPIRING_WITHIN_DAYS = 3;

export type SummaryState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly summary: PlaceSummary }
  | { readonly status: 'error'; readonly code: unknown; readonly httpStatus: number | undefined };

/**
 * Riepilogo's one read (FR-AD-8): the counts of the place's own sections, so each number matches
 * the list it opens, and the three lists of what needs attention, each with its count and its
 * most urgent items. At a tenant or an organization; the platform shows no Riepilogo (#214).
 */
export function useSummary(
  placeKind: 'tenants' | 'organizations',
  placeId: string,
): { readonly state: SummaryState; readonly reload: () => void } {
  const [state, setState] = useState<SummaryState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    void apiClient
      .GET('/v1/administration/{placeKind}/{placeId}/summary', {
        params: {
          path: { placeKind, placeId },
          query: { expiringWithinDays: EXPIRING_WITHIN_DAYS },
        },
      })
      .then(
        ({ data, error, response }) => {
          if (cancelled) {
            return;
          }
          setState(
            data !== undefined && response.ok
              ? { status: 'ready', summary: data }
              : { status: 'error', code: error, httpStatus: response.status },
          );
        },
        () => {
          if (!cancelled) {
            setState({ status: 'error', code: null, httpStatus: undefined });
          }
        },
      );
    return () => {
      cancelled = true;
    };
  }, [placeKind, placeId, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, reload };
}
